import axios from 'axios';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router';

import {config} from '@/config';
import {
  PAGE_HOTELS,
  DYNCAMIC_SCARCITY_ELEMENT_HOTELLIST_THRESHOLD,
  DYNCAMIC_SCARCITY_ELEMENT_OFFERLIST_THRESHOLD,
} from '@/consts';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';

type UseScarcityElementReturn = {
  isLoading: boolean;
  content: string;
  count?: number;
};

export const useScarcityElement = (): UseScarcityElementReturn => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const currentPage = useCurrentPage();
  const dynamicContent = 'scarcityElement.dynamic.content';
  const staticContent = 'scarcityElement.static.content';
  const [isLoading, setIsLoading] = useState(true);
  const [count, setCount] = useState(0);

  const hotelListStaticTreatmentEnabled =
    useLocalSplit('FBO_2918_static_scarcity_elements_IBE3').treatment === 'on';
  const offerListStaticEnabled =
    useLocalSplit('FBO_2919_static_scarcity_elements_IBE4_5').treatment ===
    'on';
  const hotelListDynamicEnabled =
    useLocalSplit('FBO_3080_dynamic_scarcity_elements_hotellist').treatment ===
    'on';
  const offerListDynamicEnabled =
    useLocalSplit('FBO_3328_dynscarcity_offerlist_checkout').treatment === 'on';

  const destinationId = searchParams.destinationId ?? '';
  const hotelId = params.hotelId ?? '';

  let path = `/destination/booked/hotelId/${hotelId}?interval=1m&portal=${import.meta.env.VITE_PORTAL_DOMAIN}`;
  if (currentPage === PAGE_HOTELS) {
    path = `/destination/viewed/destinationId/${destinationId}?portal=${import.meta.env.VITE_PORTAL_DOMAIN}`;
  }

  useEffect(() => {
    if (!hotelListDynamicEnabled && !offerListDynamicEnabled) {
      setIsLoading(false);
      return;
    }

    if (!destinationId && !hotelId) {
      setCount(0);
      return;
    }

    // eslint-disable-next-line promise/catch-or-return -- ...
    axios
      .get(`${import.meta.env.VITE_SERVICE_DLSS_URL}${path}`)
      .then(({data}) => setCount(data.count))
      .catch(() => setCount(0))
      .finally(() => setIsLoading(false));
  }, [
    destinationId,
    hotelId,
    hotelListDynamicEnabled,
    offerListDynamicEnabled,
    path,
  ]);

  const travelType = searchParams.travelType;

  if (currentPage === PAGE_HOTELS) {
    if (
      hotelListDynamicEnabled &&
      count >= DYNCAMIC_SCARCITY_ELEMENT_HOTELLIST_THRESHOLD
    ) {
      return {isLoading, content: dynamicContent, count};
    } else {
      return hotelListStaticTreatmentEnabled &&
        config.topRegions[travelType].includes(destinationId)
        ? {isLoading, content: staticContent}
        : {isLoading, content: ''};
    }
  } else if (
    offerListDynamicEnabled &&
    count >= DYNCAMIC_SCARCITY_ELEMENT_OFFERLIST_THRESHOLD
  ) {
    return {isLoading, content: dynamicContent, count};
  } else {
    return offerListStaticEnabled &&
      config.topHotels[travelType].includes(hotelId)
      ? {isLoading, content: staticContent}
      : {isLoading, content: ''};
  }
};
